<template>
  <div v-if="columns" class="table-flex" :style="tableWidth">
    <div class="table-flex__row table-flex__row--header" :style="{ top: `${headerPositionTop}px` }">
      <div
        v-for="(col) in columns"
        v-show="getFormattedValue(col, 'isVisible', true)"
        :key="col.slotKey"
        v-tooltip="{content: col.title, classes: col.customTooltip ? 'customTooltip' : ''}"
        class="table-flex__row__cell"
        :class="[
          getFormattedValue(col, 'isFrozen', false) ? 'table-flex__row__cell--is-frozen' : '',
          `${col.slotKey}`
        ]"
        :style="{ 'background-color': `${headerCellBackground}`, flex: `0 0 ${col.width}px`, left: `${typeof col.left !== 'undefined' ? col.left : ''}px`, 
                  right: `${typeof col.right !== 'undefined' ? col.right : ''}px`, width: `${col.width}px`}"
      >
        <span v-if="col.type === 'checkbox'">
          <span v-if="col.showTitle">{{ col.title }}</span>
          <checkbox
            v-else
            :id="`${primaryKey}-checkbox`"
            :key="`${primaryKey}-checkbox`"
            label
            :checked="isAllCheckboxEnabled(selectedRows)"
            :disabled="isAllCheckboxDisabled(selectedRows)"
            :value="col[col.slotKey] || ''"
            @onchange="(checked, event) => selectAll(checked, event)"
          />
        </span>
        <span v-else class="table-flex__row__cell__data">
          <span v-if="col.editField">
            <slot name="header-icon"></slot>
          </span>
          <span>{{ col.title }}</span>
          <span 
            v-if="col.showInfoIcon" 
            v-tooltip="{content: col.infoText, classes: col.customTooltip ? 'customTooltip' : ''}" 
          > 
            <icon name="info-circle" class="table-flex__row__cell__data__info-icon" />
          </span>
          <div
            v-if="col.sortable"
            class="table-flex__row__cell__data--is-sort"
          >
            <span
              class="mi-20px mi-chevron-up table-flex__row__cell--sort-icon"
              @click="sort(col, 'asc')"
            ></span>
            <span
              class="mi-20px mi-chevron-down table-flex__row__cell--sort-icon"
              @click="sort(col, 'desc')"
            ></span>
          </div>
        </span>
      </div>
    </div>
    <div v-if="data.length && columns">
      <div v-for="(row, index) in data" :key="row[primaryKey]" class="table-flex__row" :class="[row[primaryKey], row.rowAdditionActive ? 'table-flex__row--added': '']">
        <div
          v-for="(col) in columns"
          v-show="getFormattedValue(col, 'isVisible', true)"
          :key="col.slotKey"
          class="table-flex__row__cell"
          :class="[
            col.slotKey,
            getFormattedValue(col, 'isFrozen', false) ? 'table-flex__row__cell--is-frozen' : '',
          ]"
          :style="{ 'background-color': `${row.color ? row.color : cellBackground}`, flex: `0 0 ${col.width}px`, left: `${typeof col.left !== 'undefined' ? col.left : ''}px`, 
                    right: `${typeof col.right !== 'undefined' ? col.right : ''}px`, width: `${col.width}px`}"
        >
          <span v-if="col.type=== 'checkbox'">
            <checkbox
              :id="`${row[primaryKey]}-checkbox`"
              :key="`${row[primaryKey]}-checkbox`"
              label
              :checked="selectedRows.indexOf(row[primaryKey]) > -1"
              :disabled="isCheckboxDisabled(row)"
              :value="row[primaryKey] || ''"
              @onchange="(checked, event) => selectRow(checked, event, row)"
            />
          </span>
          <slot
            v-else-if="activeRow && activeRow[primaryKey] === row[primaryKey] && col.editableField"
            :row="row" :col="col" :index="index" 
            name="editRow"
          >
          </slot>
          <slot v-else :row="row" :col="col" :index="index"><span v-tooltip="getTableCellValue(row, col)">{{ getTableCellValue(row, col) }}</span></slot>
        </div>
      </div>
    </div>
    <div v-else class="table-no-records">{{ noDataMessage }}</div>
    <div v-if="isEditable" class="table-flex__add-item-btn" @click="addNewItem()">
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.00065 0.333984C3.31865 0.333984 0.333984 3.31865 0.333984 7.00065C0.333984 10.6827 3.31865 13.6673 7.00065 13.6673C10.6827 13.6673 13.6673 10.6827 13.6673 7.00065C13.6673 3.31865 10.6827 0.333984 7.00065 0.333984ZM10.334 7.66732H7.66732V10.334H6.33398V7.66732H3.66732V6.33398H6.33398V3.66732H7.66732V6.33398H10.334V7.66732Z"
        />
      </svg>
      <span>Add New Item</span>
    </div>
  </div>
</template>

<script>
import Checkbox from "../mds/Checkbox.vue"

export default {
  name: "Table",
  components: {
    Checkbox,
  },
  props: {
    defaultColumns: {
      type: Array,
      default: () => [],
    },
    rowData: {
      type: Array,
      default: () => [],
    },
    hasStickyHeader: {
      type: Boolean,
      default: false,
    },
    primaryKey: {
      type: String,
      default: "",
    },
    selectedRows: {
      type: Array,
      default: () => {
        return [];
      },
    },
    cellBackground: {
        type: String,
        default: '#fff'
    },
    headerCellBackground: {
        type: String,
        default: '#fff'
    },
    noDataMessage: {
      type: String,
      default: 'No Records Found'
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    tableActionColumns: {
      type: Array,
      default: () => {
        return [];
      },
    },
    configModalIsOpen: {
      type: Boolean,
      default: false
    },
    headerPositionTop: {
      type: Number,
      default: 0,
    },
    activeRow: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      columns: [],
      data: [],
      currentSortDir: "asc",
      currentSortCol: "",
    };
  },
  computed: {
    tableWidth() {
      return `width: ${this.columns
        .filter((item) => this.getFormattedValue(item, 'isVisible', true))
        .reduce((acc, col) => (acc += col.width), 0)}px`;
    },
  },
  watch: {
    defaultColumns: {
      deep: true,
      immediate: true,
      handler() {
        if(!this.configModalIsOpen) {
          const defaultColumnsCopy = JSON.parse(JSON.stringify(this.defaultColumns));
          const allColumns = [...this.tableActionColumns, ...defaultColumnsCopy];
          let leftStickyColumns = [];
          let rightStickyColumns = [];
          let currentLeftIndex = 0;
          let currentRightIndex = 0;
          const nonStickyColumns = allColumns.filter(
            (item) => !this.getFormattedValue(item, 'isFrozen', false) && this.getFormattedValue(item, 'isVisible', true)
          );
          leftStickyColumns = allColumns.filter(
              (item) => this.getFormattedValue(item, 'isFrozen', false) && this.getFormattedValue(item, 'isVisible', true) && this.getFormattedValue(item, 'frozenPosition', "left") === "left"
            ).map((item) => {
            let stickyElem;
            stickyElem = { ...item, left: currentLeftIndex };
            currentLeftIndex += item.width;
            return stickyElem;
          });
          rightStickyColumns = allColumns.filter(
              (item) => this.getFormattedValue(item, 'isFrozen', false) && this.getFormattedValue(item, 'isVisible', true) && this.getFormattedValue(item, 'frozenPosition', "left") === "right"
            ).map((item) => {
            let stickyElem;
            stickyElem = { ...item, right: currentRightIndex };
            currentRightIndex += item.width;
            return stickyElem;
          });
          rightStickyColumns.reverse();
          this.columns = [...leftStickyColumns, ...nonStickyColumns, ...rightStickyColumns];
        }
      },
    },
    rowData: {
        deep: true,
        immediate: true,
        handler() {
            this.data = [...this.rowData];
        }
    }
  },
  mounted() {
    this.hasStickyHeader
      ? this.addClassNameToGrandParent()
      : this.addClassNameToParent();
    this.hasStickyHeader && document.querySelector("#app-footer")
      ? (document.querySelector("#app-footer").style.display = "none")
      : "";
  },
  destroyed() {
    this.hasStickyHeader
      ? this.removeClassNameFromGrandParent()
      : this.removeClassNameFromParent();
    this.hasStickyHeader && document.querySelector("#app-footer")
      ? (document.querySelector("#app-footer").style.display = "flex")
      : "";
  },
  methods: {
    getFormattedValue(col, attrName, fallbackVal) {
      return Object.prototype.hasOwnProperty.call(col, attrName) ? col[attrName] : fallbackVal;
    },
    addClassNameToGrandParent() {
      this.$emit("add-table-style", "table-style");
    },
    addClassNameToParent() {
      this.$emit("addParentClass", "table-parent-style");
    },
    removeClassNameFromGrandParent() {
      this.$emit("add-table-style", "");
    },
    removeClassNameFromParent() {
      this.$emit("addParentClass", "");
    },
    getTableCellValue(row, col) {
      const value = row[col.slotKey];
      return !value || typeof value === "undefined" || value === "null"
        ? "-"
        : value;
    },
    sort(col, sortOrder) {
      this.$emit("onSortColumns", col.slotKey, sortOrder, col.title);
      // if (this.currentSortCol === col.slotKey) {
      //   this.currentSortDir = this.currentSortDir === "asc" ? "desc" : "asc";
      // } else {
      //   this.currentSortCol = col.slotKey;
      // }
      // this.data.sort(this.sortBy(col.slotKey, col.type, this.currentSortDir));
    },
    sortBy(property, type, order) {
      this.currnetSortDir = order;
      return function (a, b) {
        if (type === "number") {
          return order === "desc"
            ? a[property] - b[property]
            : (a[property] - b[property]) * -1;
        } else if (type === "date") {
          const varA = new Date(a[property]);
          const varB = new Date(b[property]);
          return order === "desc" ? varA - varB : (varA - varB) * -1;
        } else {
          const varA =
            typeof a[property] === "string"
              ? a[property].toUpperCase()
              : a[property];
          const varB =
            typeof b[property] === "string"
              ? b[property].toUpperCase()
              : b[property];
          let comparison = 0;
          if (varA > varB) comparison = 1;
          else if (varA < varB) comparison = -1;
          return order === "desc" ? comparison * -1 : comparison;
        }
      };
    },
    onCellEdit(row, col) {
      if (
        col?.canEdit &&
        ((this.activeRow && this.activeRow[this.primaryKey] !== row[this.primaryKey]) ||
          !this.activeRow)
      ) {
        this.$emit("doubleClickCallback", row, col);
      }
    },
    selectAll(checked, event) {
      let availableRows = [];
      if (checked) {
        availableRows = this.data.filter(
          (item) => !this.isCheckboxDisabled(item)
        );
      }
      this.$emit("selectAllEvent", availableRows, checked, event);
    },
    selectRow(checked, event, row) {
      let selectedRows = [...this.selectedRows];
      const existingRow = selectedRows.indexOf(row[this.primaryKey]);
      if (checked) {
        if (existingRow > -1) {
          selectedRows[existingRow] = row[this.primaryKey];
        } else {
          selectedRows.push(row[this.primaryKey]);
        }
      } else if (existingRow > -1) {
        selectedRows.splice(existingRow, 1);
      }
      const selectedItems = this.data.filter(
        (item) => selectedRows.indexOf(item[this.primaryKey]) > -1
      );
      this.$emit("selectChangeEvent", selectedItems, row, checked, event);
    },
    isCheckboxDisabled(row) {
      return this.$parent.checkIsDisabled
        ? this.$parent.checkIsDisabled(row)
        : false;
    },
    isAllCheckboxEnabled(selectedRows) {
      let availableRows = this.data.filter(
        (item) => !this.isCheckboxDisabled(item)
      );
      return availableRows.length
        ? selectedRows.length >= availableRows.length
        : false;
    },
    isAllCheckboxDisabled() {
      let availableRows = this.data.filter(
        (item) => !this.isCheckboxDisabled(item)
      );
      return !availableRows.length;
    },
    addNewItem(){
        this.$emit('insertEvent', this.columns, this.addNewItemCallback)
    },
    addNewItemCallback(addedItem) {
      this.data = [...this.data, {...addedItem}];
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/imports";

$table-border: #e8eaec;
.table-flex {
  font-family: Maersk Text;
  color: var(--invert-color);
  font-size: 14px;
  box-sizing: content-box;
  border-radius: 0.25rem;
  position: relative;
  border: 1px solid $table-border;
  &__row {
    $self: &;
    display: flex;
    border-bottom: 1px solid $table-border;
    &__cell {
      padding: 4px 12px;
      height: 48px;
      line-height: 24px;
      display: flex;
      align-items: center;
      box-sizing: border-box;
      &--is-frozen {
        position: -webkit-sticky;
        position: sticky;
        z-index: 1;
      }
      &__data {
        display: flex;
        align-items: center;
        &__info-icon{
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 10px;
        }
        &--is-sort {
          display: flex;
          flex-direction: column; 
        }
      }
      span{
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }
    &--header {
      z-index: 2;
      position: sticky;
      #{ $self }__cell {
        font-weight: 700;
        &--sort-icon {
          margin-left: 10px;
          position: relative;
          top: -5px;
          cursor: pointer;
          &:first-child {
            top: 5px;
          }
        }
      }
    }
  }
  &__add-item-btn{
    color: #353535;
    background-color: #fff;
    box-shadow: none;
    font-size: 14px;
    line-height: 24px;
    padding: 5px 12px;
    text-decoration: none;
    transition: all 0.2s ease-in-out;
    border: 1px solid #cfcfcf;
    border-radius: 5px;
    cursor: pointer;
    position: sticky;
    left: 10px;
    margin: 8px 10px;
    width: fit-content;
    display: flex;
    align-items: center;
    svg{
      margin-right: 4px;
    }
  }
}
.table-no-records {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 24px;
  position: sticky;
  left: 0;
  width: fit-content;
}
</style>

<style lang="scss">
.tooltip {
  &.customTooltip {
    .tooltip-inner {
      background: #fff;
      color: #545454;
      border: 1px solid #dbdbdb;
      font-family: "Maersk Headline";
      font-size: 13px;
      padding: 10px;
    }
  }
}
</style>
